import React from "react";

import { Box, Flex, ScrollArea, Tabs } from "@mantine/core";
import { useHotkeys, useMediaQuery, useToggle } from "@mantine/hooks";
import { IconBug, IconChecklist, IconPalette, IconPlane, IconSettings } from "@tabler/icons-react";
import { Helmet } from "react-helmet";

import { useCurrentEditorDocument } from "../../hooks/useEditorState";
import { RealisticEditor } from "../editor/ChecklistDocumentEditor";
import { EditorSettings } from "../editor/EditorSettings";
import { StyleSettings } from "../editor/StyleSettings";
import { ChecklistDebugState } from "../renderer/debug/ChecklistDebugState";
import { PDFPreview } from "../renderer/pdf/PDFPreview";

export const EditorPage = (): JSX.Element => {
  // 1200px is the default large breakpoint
  // reasonable width for editor + fit preview with scrolling + sidebar
  const fullWidth = useMediaQuery(`(min-width: calc(500px + 6.5in + 300px))`, true, {
    getInitialValueInEffect: false,
  });

  const debugToggleValues =
    import.meta.env.DEV ? ([true, false] as const) : ([false, true] as const);
  const [debugShowing, toggleDebugShowing] = useToggle(debugToggleValues);
  useHotkeys([["alt+mod+shift+X", () => toggleDebugShowing()]]);

  const documentTitle = useCurrentEditorDocument((d) => d.title, null);

  const editorTab = [
    <Tabs.Tab value="editor" icon={<IconChecklist />}>
      Editor
    </Tabs.Tab>,
    <Tabs.Panel value="editor">
      <RealisticEditor />
    </Tabs.Panel>,
  ];

  const stylesTab = [
    <Tabs.Tab value="styles" icon={<IconPalette />}>
      Styles
    </Tabs.Tab>,
    <Tabs.Panel value="styles">
      <StyleSettings />
    </Tabs.Panel>,
  ];

  const settingsTab = [
    <Tabs.Tab value="settings" icon={<IconSettings />}>
      Settings
    </Tabs.Tab>,
    <Tabs.Panel value="settings">
      <EditorSettings />
    </Tabs.Panel>,
  ];

  const previewTab = [
    <Tabs.Tab value="preview" icon={<IconPlane />}>
      Preview
    </Tabs.Tab>,
    <Tabs.Panel value="preview" sx={{ height: "calc(100vh - 106px)" }}>
      <PDFPreview />
    </Tabs.Panel>,
  ];

  const debugTab = debugShowing
    ? [
        <Tabs.Tab value="debug" icon={<IconBug />}>
          Debug
        </Tabs.Tab>,
        <Tabs.Panel value="debug">
          <ChecklistDebugState />
        </Tabs.Panel>,
      ]
    : [<></>, <></>];

  const metadata = (
    <Helmet>
      <title>{documentTitle ? `Editing ${documentTitle}` : "Editor"}</title>
    </Helmet>
  );

  if (fullWidth) {
    return (
      <>
        {metadata}
        <Flex wrap={"nowrap"} align={"start"}>
          <Box
            style={{ flex: "5 0 575px" }}
            sx={(theme) => ({ borderRight: `1px solid ${theme.colors.gray[5]}` })}
          >
            <Tabs defaultValue="editor">
              <Tabs.List>
                {editorTab[0]}
                {stylesTab[0]}
                {settingsTab[0]}
              </Tabs.List>
              <ScrollArea
                sx={(theme) => ({
                  height: "calc(100vh - 106px)",
                  padding: `0 ${theme.spacing.xs}px`,
                })}
                type="always"
              >
                {editorTab[1]}
                {stylesTab[1]}
                {settingsTab[1]}
              </ScrollArea>
            </Tabs>
          </Box>
          <Box style={{ flex: "0 5 9.5in", overflowX: "auto" }}>
            <Tabs defaultValue="preview">
              <Tabs.List>
                {previewTab[0]}
                {debugTab[0]}
              </Tabs.List>
              <ScrollArea
                // style={{ flex: "0 1 9.5in" }}
                sx={() => ({
                  height: "calc(100vh - 106px)",

                  // minWidth: "9.5in",
                })}
              >
                {previewTab[1]}
                {debugTab[1]}
              </ScrollArea>
            </Tabs>
          </Box>
        </Flex>
      </>
    );
  } else {
    return (
      <>
        {metadata}
        <Tabs defaultValue="editor">
          <Tabs.List>
            {editorTab[0]}
            {stylesTab[0]}
            {settingsTab[0]}
            {previewTab[0]}
            {debugTab[0]}
          </Tabs.List>
          <ScrollArea sx={{ height: "calc(100vh - 106px)" }} type="always">
            {editorTab[1]}
            {stylesTab[1]}
            {settingsTab[1]}
            {previewTab[1]}
            {debugTab[1]}
          </ScrollArea>
        </Tabs>
      </>
    );
  }
};
